import { useState, useEffect } from 'react';
import { useDashboardStore } from '@/features/dashboard/stores/dashboard.store';
import { useMappingsStore } from '@/features/form-builder/store/mappings.store';
import { useDashboardPrerequisites } from '@/features/dashboard/hooks/use-dashboard-prerequisites';
import { useRecordsSearch } from '@/features/dashboard/hooks/use-records-search';
import { useCrmUsers } from '@/features/dashboard/hooks/use-crm-users';
import { toast } from 'react-hot-toast';
import { logger } from '@/features/help/utils/logger';
import { Copy, ExternalLink, CheckCircle, HelpCircle } from 'lucide-react';
import { MaterialTooltip } from '@/features/ui/components/MaterialTooltip';
import { LoadingSpinner } from '@/features/help/components/LoadingSpinner';
import { MaterialSelect } from '@/features/ui/components/MaterialSelect';
import { MaterialAutocomplete } from '@/features/ui/components/MaterialAutocomplete';
import { InfoTooltip } from '@/features/ui/components/Tooltip';
import { FieldMappingSummary } from '@/features/form-builder/types';
import { InitialSetupModal } from './InitialSetupModal';
import { APPLICATION_URL_HELP_TEXT } from '@/features/dashboard/constants/application-url-help-text.dashboard.constants';

export const Dashboard = () => {
  const {
    selectedFieldMappingId,
    selectedRecordId,
    selectedRecordOwnerId,
    signerIndex,
    loading,
    selectFieldMapping,
    setSelectedRecordId,
    setSelectedRecordOwnerId,
    setSignerIndex,
    generateApplicationUrlForSelectedMapping,
  } = useDashboardStore();

  // Keep signerIndex unset until a specific record is selected

  const { mappingSummaries } = useMappingsStore();

  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [formFormat, setFormFormat] = useState<'interactive' | 'pdf'>('interactive');
  const [shouldRenderSignerIndex, setShouldRenderSignerIndex] = useState(false);
  const showAdvancedUrlOptions = true;

  // State for the initial setup modal
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [isInitialSetupModalOpen, setIsInitialSetupModalOpen] = useState(false);

  // Get prerequisite status
  const { dashboardInitialized, prerequisitesMet } = useDashboardPrerequisites();

  // Use TanStack Query for records search
  const { records, setSearchQuery, isLoading: isLoadingRecords } = useRecordsSearch({
    mappingId: selectedFieldMappingId,
    enabled: !!selectedFieldMappingId,
  });

  // Use TanStack Query for CRM users
  const { users: crmUsers, isLoading: isLoadingUsers } = useCrmUsers();

  useEffect(() => {
    if (selectedRecordId) {
      setShouldRenderSignerIndex(true);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShouldRenderSignerIndex(false);
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [selectedRecordId]);

  // Auto-select first field mapping when available mappings are loaded
  useEffect(() => {
    if (mappingSummaries.length > 0 && !selectedFieldMappingId) {
      const firstMapping = mappingSummaries[0];
      logger.debug('Dashboard', 'Auto-selecting first available field mapping', {
        mappingId: firstMapping.mapping_id,
        mappingName: firstMapping.mapping_name,
      });
      selectFieldMapping(firstMapping.mapping_id, firstMapping.short_id);
    }
  }, [mappingSummaries, selectedFieldMappingId, selectFieldMapping]);

  // Effect to show the initial setup modal once if needed
  useEffect(() => {
    logger.debug('Dashboard', 'Checking initial setup modal conditions', {
      dashboardInitialized,
      prerequisitesMet,
      initialCheckDone,
    });

    if (dashboardInitialized && !initialCheckDone) {
      if (!prerequisitesMet) {
        logger.info('Dashboard', 'Initial setup prerequisites not met. Opening setup modal.');
        setIsInitialSetupModalOpen(true);
      }
      setInitialCheckDone(true);
    }
  }, [dashboardInitialized, prerequisitesMet, initialCheckDone]);

  /**
   * Handles copying text to clipboard with visual feedback
   */
  const handleCopyWithFeedback = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(successMessage);
      toast.success(successMessage);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (error) {
      logger.error('Dashboard', 'Failed to copy to clipboard', { error });
      toast.error('Failed to copy to clipboard');
    }
  };

  /**
   * Builds a copyable URL template for customers to embed in their app.
   */
  const getUrlTemplateText = (): string => {
    if (!selectedFieldMappingId) {
      return '';
    }

    const baseUrl = generateApplicationUrlForSelectedMapping() || '';
    if (!baseUrl) {
      return '';
    }

    const basePath = baseUrl.split('?')[0];
    return `${basePath}?crmId={CRM_RECORD_ID}&signerIndex={SIGNER_INDEX}&recordOwnerId={RECORD_OWNER_ID}&pdfOnly=1`;
  };

  /**
   * Generates the final URL based on current selections
   * Returns base URL if no record selected, or full URL with query params if record selected
   * Note: We reference signerIndex and selectedRecordOwnerId here to ensure React re-renders when they change
   */
  const getGeneratedUrl = (): string => {
    if (!selectedFieldMappingId) {
      return '';
    }

    // These are used by generateApplicationUrlForSelectedMapping internally,
    // but we reference them here to trigger re-renders when they change
    void signerIndex;
    void selectedRecordOwnerId;
    void formFormat;

    // If record is selected, generate URL with actual values; otherwise return base URL
    let url = selectedRecordId
      ? generateApplicationUrlForSelectedMapping(selectedRecordId) || ''
      : generateApplicationUrlForSelectedMapping() || '';

    // Add pdfOnly parameter if PDF format is selected
    if (formFormat === 'pdf' && url) {
      const hasQuery = url.includes('?');
      url += hasQuery ? '&pdfOnly=1' : '?pdfOnly=1';
    }

    return url;
  };

  /**
   * Handles copy template button click
   */
  const handleCopyTemplate = () => {
    const template = getUrlTemplateText();
    if (template) {
      handleCopyWithFeedback(template, 'URL template copied!');
    } else {
      toast.error('Please select a mapping first');
    }
  };

  /**
   * Handles copy link button click
   */
  const handleCopyLink = () => {
    const url = getGeneratedUrl();
    if (url) {
      const message = formFormat === 'pdf' ? 'PDF-only URL copied!' : 'URL copied to clipboard!';
      handleCopyWithFeedback(url, message);
    } else {
      toast.error('Please select a mapping first');
    }
  };

  /**
   * Handles test link button click - opens URL in new tab
   */
  const handleTestLink = () => {
    const url = getGeneratedUrl();
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('Please select a mapping first');
    }
  };

  // Get the currently selected record and owner for display
  const selectedRecord = records.find((record: any) => record.id === selectedRecordId);
  const selectedOwner = crmUsers.find((user) => user.id === selectedRecordOwnerId);

  // Check if URL can be generated - only requires mapping to be selected (Section 3 is optional)
  const canGenerateUrl = !!selectedFieldMappingId;

  // Check if we can generate any URL (just need a mapping selected)
  const canTestUrl = !!selectedFieldMappingId;
  const canShowUrlTemplate = !!selectedFieldMappingId;

  return (
    <>
      <InitialSetupModal
        isOpen={isInitialSetupModalOpen}
        onClose={() => setIsInitialSetupModalOpen(false)}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6 min-w-0">
        {/* Section 1: Set Form Context */}
        <section className="bg-white border border-neutral-200 rounded-xl p-6 shadow-sm">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-neutral-900">1. Set Form Context</h2>
            <p className="text-sm text-neutral-500 mt-1">
              Define the base structure of the form and the data source.
            </p>
          </div>

          <MaterialSelect
            label="Field Mapping"
            value={selectedFieldMappingId || ''}
            onChange={(value) => {
              const selectedMapping = mappingSummaries.find((m) => m.mapping_id === value);
              selectFieldMapping(value || null, selectedMapping?.short_id || null);
              // Reset all Section 3 selections when mapping changes (signer index defaults to 1)
              setSelectedRecordId(null);
              setSelectedRecordOwnerId(null);
              setSignerIndex(1);
              setFormFormat('interactive');
            }}
            options={mappingSummaries.map((mapping: FieldMappingSummary) => ({
              value: mapping.mapping_id,
              label: mapping.mapping_name,
            }))}
            placeholder="Select a mapping..."
          />
        </section>

        {/* Section 2: URL Template */}
        {canShowUrlTemplate && (
          <section className="bg-white border border-neutral-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">2. URL Template for Your App</h2>
                <p className="text-sm text-neutral-500 mt-1">
                  Copy this template and replace each placeholder with real values.
                </p>
              </div>
              <InfoTooltip text={APPLICATION_URL_HELP_TEXT} className="mt-1" />
            </div>

            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 mb-4 flex items-start gap-3">
              <p className="text-sm text-neutral-700 font-mono break-all whitespace-normal flex-1">
                {getUrlTemplateText()}
              </p>
              <button
                type="button"
                onClick={handleCopyTemplate}
                disabled={loading}
                aria-label="Copy URL template"
                className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-md border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Copy URL template"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>

            <button
              onClick={handleCopyTemplate}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <LoadingSpinner className="h-5 w-5" />
              ) : (
                <>
                  <Copy className={`h-5 w-5 ${copySuccess ? 'text-white' : ''}`} />
                  Copy URL Template
                </>
              )}
            </button>
          </section>
        )}

        {showAdvancedUrlOptions && (
          <>
            <div className="flex items-center justify-center">
              <span className="text-xs font-semibold uppercase tracking-wide text-neutral-400">Or</span>
            </div>

            {/* Section 3: Customize URL Options */}
            <section className="bg-white border border-neutral-200 rounded-xl p-6 shadow-sm">
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-neutral-900">3. Customize URL Options</h2>
                <p className="text-sm text-neutral-500 mt-1">
                  Configure optional parameters for the final link.
                </p>
              </div>
              <div className="space-y-6">
                {/* Select Specific Record to Pre-fill */}
                <div>
                  <MaterialAutocomplete
                    label="Select Specific Record to Pre-fill"
                    value={
                      selectedRecord
                        ? {
                            ...selectedRecord,
                            label: selectedRecord.name,
                          }
                        : null
                    }
                    onChange={(record) => {
                      const nextRecordId = record?.id || null;
                      setSelectedRecordId(nextRecordId);

                      if (nextRecordId) {
                        if (signerIndex === null) {
                          setSignerIndex(1);
                        }
                      } else {
                        setSignerIndex(null);
                        if (formFormat === 'pdf') {
                          setFormFormat('interactive');
                        }
                      }
                    }}
                    onInputChange={(inputValue) => {
                      setSearchQuery(inputValue);
                    }}
                    options={records.map((record: any) => ({
                      ...record,
                      label: record.name,
                    }))}
                    placeholder="Search records by name or email..."
                    loading={isLoadingRecords}
                    disabled={!selectedFieldMappingId}
                    noOptionsText="No records found."
                    freeSolo={false}
                  />
                  {selectedRecord && (
                    <p className="text-xs text-neutral-400 mt-1.5 ml-1">
                      (Found record ID: {selectedRecord.id.substring(0, 15)}...)
                    </p>
                  )}

                  {shouldRenderSignerIndex && (
                    <div
                      className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${
                        selectedRecordId ? 'max-h-[200px]' : 'max-h-0'
                      }`}
                    >
                      <div
                        className={`transition-[opacity,transform] duration-300 ease-in-out ${
                          selectedRecordId
                            ? 'opacity-100 translate-y-0 pt-6'
                            : 'opacity-0 -translate-y-1 pt-0'
                        }`}
                      >
                        <MaterialAutocomplete
                          label="Signer Index"
                          value={
                            signerIndex !== null
                              ? {
                                  id: String(signerIndex),
                                  label:
                                    signerIndex === 1 ? '1 (Primary Signer)' : String(signerIndex),
                                }
                              : null
                          }
                          onChange={(option) => {
                            if (option) {
                              setSignerIndex(parseInt(option.id, 10));
                            } else {
                              setSignerIndex(null);
                            }
                          }}
                          options={[
                            { id: '1', label: '1 (Primary Signer)' },
                            { id: '2', label: '2' },
                            { id: '3', label: '3' },
                            { id: '4', label: '4' },
                            { id: '5', label: '5' },
                          ]}
                          placeholder="Select signer index..."
                          noOptionsText="No options."
                          freeSolo={false}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Select Record Owner */}
                <div>
                  <MaterialAutocomplete
                    label="Select Record Owner"
                    value={
                      selectedOwner
                        ? { id: selectedOwner.id, label: selectedOwner.name || selectedOwner.email }
                        : null
                    }
                    onChange={(option) => {
                      setSelectedRecordOwnerId(option?.id || null);
                    }}
                    options={crmUsers.map((user) => ({
                      id: user.id,
                      label: user.name || user.email,
                    }))}
                    placeholder="Select an owner..."
                    loading={isLoadingUsers}
                    disabled={!selectedFieldMappingId}
                    noOptionsText="No owners found."
                    freeSolo={false}
                  />
                  {selectedOwner && (
                    <p className="text-xs text-neutral-400 mt-1.5 ml-1">
                      (Owner ID: {selectedOwner.id.substring(0, 15)}...)
                    </p>
                  )}
                </div>

                {/* Form Format */}
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-3">
                    Form Format
                  </label>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="formFormat"
                        value="interactive"
                        checked={formFormat === 'interactive'}
                        onChange={() => setFormFormat('interactive')}
                        className="w-4 h-4 text-brand-600 border-neutral-300 focus:ring-brand-500"
                      />
                      <span className="ml-2 text-sm text-neutral-700">Normal Mode</span>
                    </label>
                    <label
                      className={`flex items-center ${
                        !selectedRecordId ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                      }`}
                    >
                      <input
                        type="radio"
                        name="formFormat"
                        value="pdf"
                        checked={formFormat === 'pdf'}
                        onChange={() => setFormFormat('pdf')}
                        disabled={!selectedRecordId}
                        className="w-4 h-4 text-brand-600 border-neutral-300 focus:ring-brand-500 disabled:cursor-not-allowed"
                      />
                      <span className="ml-2 text-sm text-neutral-700 flex items-center gap-1">
                        PDF-only Mode
                        <MaterialTooltip
                          title={
                            !selectedRecordId
                              ? 'Select a specific record first to use PDF-only mode'
                              : 'PDF-only mode generates only a PDF and does not update any CRM fields.'
                          }
                          placement="top"
                          leaveTouchDelay={5000}
                        >
                          <HelpCircle className="h-3.5 w-3.5 text-neutral-400 cursor-help" />
                        </MaterialTooltip>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 4: Generated URL Ready */}
            {canGenerateUrl && (
              <section className="bg-green-50 border border-green-200 rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h2 className="text-lg font-semibold text-green-800">Generated URL Ready</h2>
                </div>

                <div className="bg-white border border-green-200 rounded-lg p-3 mb-4 overflow-hidden">
                  <p className="text-sm text-neutral-700 font-mono truncate">{getGeneratedUrl()}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleCopyLink}
                    disabled={loading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <LoadingSpinner className="h-5 w-5" />
                    ) : (
                      <>
                        <Copy className={`h-5 w-5 ${copySuccess ? 'text-white' : ''}`} />
                        Copy Link
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleTestLink}
                    disabled={!canTestUrl}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-neutral-300 hover:bg-neutral-50 text-neutral-700 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={!canTestUrl ? 'Select a mapping to test the link' : ''}
                  >
                    <ExternalLink className="h-5 w-5" />
                    Test Link
                  </button>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </>
  );
};
